import * as Sentry from '@sentry/react';
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Field, InjectedFormProps, SubmissionError, reduxForm } from 'redux-form';

import { notify } from '@/actions/ui';
import Form from '@/components/Form';
import { TextField } from '@/components/FormAdapters';
import SelectLocation from '@/components/SelectLocation';
import { profileSaveErrorMessage } from '@/profile/profileSaveFailure';
import { updateProfile } from '@/profile/store';
import { Country } from '@/reducers/countries';

const SAVE_ERROR = 'An error occurred when updating your location.';

// The `countries` query returns every expert_countries row (MetaService.AllRegions),
// which includes location_type='region' entries such as "North America" and "MENA".
// updateProfile resolves the submitted name with FindCountryByName, which only matches
// location_type='country', so those entries can never be saved. country_iso2_code is
// populated on country rows and null on region rows, so it is an exact filter — the
// same discriminator PhoneInput already uses.
export function selectableCountries<T extends Pick<Country, 'country_iso2_code'>>(
  countries: T[] = []
): T[] {
  return countries.filter((country) => Boolean(country.country_iso2_code));
}

function validate(values = {}) {
  const errors = {};

  // @ts-expect-error TS(2339): Property 'country' does not exist on type '{}'.
  if (!values.country) {
    // @ts-expect-error TS(2339): Property 'country' does not exist on type '{}'.
    errors.country = 'A valid Country is required';
  }

  return errors;
}

interface LocationFormProps {
  title?: string;
  open?: boolean;
  component?: React.ComponentType<any>;
  profileId: string;
  saveMessage?: string;
  style?: React.CSSProperties;
  onClose?: () => void;
}

export class LocationForm extends PureComponent<
  LocationFormProps & InjectedFormProps<{}, LocationFormProps>
> {
  handleSubmit = async (values: any): Promise<void> => {
    // @ts-expect-error TS(2339): Property 'profileId' does not exist on type 'Reado... Remove this comment to see the full error message
    const { profileId, updateProfile, onSubmit, notify } = this.props;

    try {
      await updateProfile({ id: profileId, ...values });
    } catch (err) {
      // Report explicitly: this used to reach Sentry only by escaping as an
      // onunhandledrejection, so handling it must not make the failure invisible.
      Sentry.captureException(err);
      // Surface the failure, and do not run onSubmit — that is what closes the
      // Edit Location dialog and lets SaveForm report "Location updated."
      const message = profileSaveErrorMessage(err, SAVE_ERROR);
      notify(message, 'error');
      // SubmissionError specifically: redux-form resolves with its `errors`, so the
      // callers' `if (errors) return` skips the success path. A raw Error would hit
      // handleSubmit's `throw submitError` branch and leak all over again.
      throw new SubmissionError({ _error: message });
    }

    // Closing the Edit Location dialog is owned by this callback, not by EditDialog:
    // EditDialog reads onSubmit()'s return synchronously and early-returns on the
    // now-pending promise, so it never closes the dialog itself.
    if (onSubmit) {
      onSubmit(values);
    }
  };

  handleReset = (): void => {
    // @ts-expect-error TS(2339): Property 'reset' does not exist on type 'Readonly<... Remove this comment to see the full error message
    const { reset, onReset } = this.props;
    reset();

    if (onReset) {
      onReset();
    }
  };

  render(): React.ReactNode {
    const {
      component: Container = Form,
      // @ts-expect-error TS(2339): Property 'allCountries' does not exist on type 'Re... Remove this comment to see the full error message
      allCountries,
      handleSubmit,
      // @ts-expect-error TS(2339): Property 'profile' does not exist on type 'Readonl... Remove this comment to see the full error message
      profile,
      ...other
    } = this.props;

    return (
      <Container {...other} onSubmit={handleSubmit(this.handleSubmit)} onReset={this.handleReset}>
        <Field
          id="locationCountry"
          component={SelectLocation}
          multiple={false}
          name="country"
          label="In which country are you located?"
          countries={allCountries}
          format={(country: any) => allCountries.find((c: any) => c.name === country)}
          parse={(country: any) => country && country.name}
        />
        <Field
          id="locationCity"
          fullWidth
          component={TextField}
          name="city"
          label="In which city are you located?"
          inputProps={{ maxLength: 255 }}
          placeholder="Start typing the name of a city here"
        />
      </Container>
    );
  }
}

const WrappedLocationForm = reduxForm<{}, LocationFormProps>({
  form: 'expertLocation',
  enableReinitialize: true,
  validate,
})(LocationForm);

export default connect(
  (state, ownProps) => {
    // @ts-expect-error TS(2571): Object is of type 'unknown'.
    const profile = state.profiles.fullProfiles[ownProps.profileId];
    return {
      profile,
      initialValues: {
        country: profile.country,
        city: profile.city,
      },
      // @ts-expect-error TS(2571): Object is of type 'unknown'.
      allCountries: selectableCountries(state.countries),
    };
  },
  {
    updateProfile,
    notify,
  }
)(WrappedLocationForm);
